import { metaAddToCart } from "@/lib/metaPixel";
import { useRef, createContext, useContext, useState, ReactNode, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/siteTracking";
import { applyQuantityRules, type QuantityRulesStatus } from "@/lib/quantityPriceRules";
import { useQuantityRules } from "@/hooks/useQuantityRules";
import { safeStorage, parseStoredCart as parseCart, parseStoredPackaging, isSameCartLine } from "@/lib/cartStorage";
import { supabase } from "@/integrations/supabase/client";
import { planCartAdd, showCartAddedNotice, showCartAddError, type CartAddOutcome } from "@/lib/cartAdd";
import { announceAvailabilityChange, checkCartAddition } from '@/lib/siteAvailability';
export { safeStorage, isSameCartLine } from "@/lib/cartStorage";

/** Nome legível da página de onde o item foi adicionado (grade, produto, vídeos, looks...). */
const describeSourcePage = (): string => {
  if (typeof window === "undefined") return "desconhecido";
  const p = window.location.pathname;
  if (p === "/") return "home";
  if (p.startsWith("/produto")) return "pagina_produto";
  if (p.startsWith("/videos")) return "videos";
  if (p.startsWith("/looks")) return "looks";
  if (p.startsWith("/busca")) return "busca";
  if (p.startsWith("/categorias")) return "categorias";
  if (p.startsWith("/checkout")) return "checkout";
  if (p.startsWith("/colecao") || p.startsWith("/lancamentos") || p.startsWith("/promocoes")) return `grade${p}`;
  return p;
};

export interface CartItem {
  id: string | number;
  name: string;
  price: number;
  size: string;
  quantity: number;
  image: string;
  weight_grams?: number;
  colorVariantId?: string;
  sizeVariantId?: string;
  colorName?: string;
  maxStock?: number;
  sku?: string;
  /** Preço unitário antes das regras de quantidade (já com promoção normal) */
  basePrice?: number;
  /** Nome da regra de quantidade aplicada, quando houver */
  bulkRuleName?: string;
  bulkRuleId?: string;
}

export interface SavedShipping {
  method: string;
  cost: number;
  label?: string;
  originalCost?: number;
  deliveryEstimate?: string;
  /** CEP (só números) da cotação; seleção de outro CEP não vale para finalizar. */
  cep?: string;
}

export interface SavedPackaging {
  productId: string;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  /** Fonte única de adição do site: confere estoque, mostra o aviso único e devolve o resultado. */
  addItem: (item: CartItem, opts?: { notify?: boolean }) => Promise<CartAddOutcome>;
  removeItem: (id: string | number, size: string, colorVariantId?: string) => void;
  updateQuantity: (id: string | number, size: string, quantity: number, colorVariantId?: string) => void;
  updateMaxStock: (id: string | number, size: string, maxStock: number, colorVariantId?: string) => void;
  clearCart: () => void;
  total: number;
  itemCount: number;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  savedShipping: SavedShipping | null;
  setSavedShipping: (shipping: SavedShipping | null) => void;
  savedPackaging: SavedPackaging | null;
  setSavedPackaging: (packaging: SavedPackaging | null) => void;
  /** Situação da carga dos descontos por quantidade ("error" bloqueia finalizar). */
  quantityRulesStatus: QuantityRulesStatus;
  retryQuantityRules: () => void;
}

const CART_STORAGE_KEY = "miahma-cart";
const PACKAGING_STORAGE_KEY = "miahma-packaging";

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    if (typeof window === "undefined") return [];
    const raw = safeStorage.get(CART_STORAGE_KEY);
    const list = parseCart(raw) as unknown as CartItem[];
    if (raw && list.length === 0) safeStorage.remove(CART_STORAGE_KEY);
    // Legacy cart photos may have come from secondary-first public responses.
    return list.map((item) => ({ ...item, image: "" }));
  });
  // Fonte síncrona do carrinho: toda mutação passa por updateItems, então decisões de
  // adição (retorno, aviso, analytics) usam o estado já aplicado, mesmo com chamadas seguidas
  // antes do rerender. Updaters são puros: nenhum efeito colateral dentro deles.
  const itemsRef = useRef(items);
  const adding = useRef(new Set<string>());
  const cartGeneration = useRef(0);
  useEffect(() => () => { cartGeneration.current += 1; }, []);
  const updateItems = (fn: (prev: CartItem[]) => CartItem[]) => {
    const prev = itemsRef.current;
    const next = fn(prev);
    if (next === prev) return;
    itemsRef.current = next;
    setItems(next);
  };
  const [isCartOpen, setIsCartOpen] = useState(false);
  const photoVariantIds = [...new Set(items.map((item) => item.colorVariantId)
    .filter((id): id is string => !!id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)))].sort().join(",");
  useEffect(() => {
    if (!photoVariantIds) return;
    let disposed = false;
    void supabase.rpc("site_cart_normal_covers", { p_variant_ids: photoVariantIds.split(",") }).then(({ data, error }) => {
      if (disposed || error) return;
      const covers = new Map((data ?? []).map((row) => [row.variant_id, row.image_url]));
      updateItems((prev) => {
        let changed = false;
        const next = prev.map((item) => {
          if (!item.colorVariantId || !photoVariantIds.split(",").includes(item.colorVariantId)) return item;
          const image = covers.get(item.colorVariantId) ?? "";
          if (image === item.image) return item;
          changed = true;
          return { ...item, image };
        });
        return changed ? next : prev;
      });
    });
    return () => { disposed = true; };
  }, [photoVariantIds]);
  const [savedShipping, setSavedShippingState] = useState<SavedShipping | null>(null);
  const [savedPackaging, setSavedPackagingState] = useState<SavedPackaging | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = safeStorage.get(PACKAGING_STORAGE_KEY);
    const p = parseStoredPackaging(raw);
    if (raw && !p) safeStorage.remove(PACKAGING_STORAGE_KEY);
    return p;
  });

  // Persist cart to localStorage
  useEffect(() => {
    safeStorage.set(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);


  // Persist packaging to localStorage
  useEffect(() => {
    if (savedPackaging) {
      safeStorage.set(PACKAGING_STORAGE_KEY, JSON.stringify(savedPackaging));
    } else {
      safeStorage.remove(PACKAGING_STORAGE_KEY);
    }
  }, [savedPackaging]);

  const setSavedShipping = (shipping: SavedShipping | null) => {
    setSavedShippingState(shipping);
  };

  const setSavedPackaging = (packaging: SavedPackaging | null) => {
    setSavedPackagingState(packaging && packaging.productId && Number.isInteger(packaging.quantity) && packaging.quantity > 0 ? packaging : null);
  };

  // Normalize cart item keys for consistent matching
  const normalizeId = (id: string | number) => String(id);
  const normalizeColorVariantId = (cvId?: string) => cvId || '';

  const findExistingIndex = (items: CartItem[], newItem: CartItem) => {
    const nId = normalizeId(newItem.id);
    const nCvId = normalizeColorVariantId(newItem.colorVariantId);
    return items.findIndex(
      (item) => normalizeId(item.id) === nId && item.size === newItem.size && normalizeColorVariantId(item.colorVariantId) === nCvId
    );
  };

  const addItem = async (newItem: CartItem, opts?: { notify?: boolean }): Promise<CartAddOutcome> => {
    const key = `${newItem.id}:${newItem.colorVariantId ?? ''}:${newItem.size}`;
    if (adding.current.has(key)) return { kind: 'busy', added: 0 };
    adding.current.add(key);
    const generation = cartGeneration.current;
    try {
      let already = itemsRef.current.find(i => isSameCartLine(i, newItem.id, newItem.size, newItem.colorVariantId))?.quantity ?? 0;
      let available = await checkCartAddition(newItem.sizeVariantId, already + newItem.quantity);
      // Quantity controls may change the cart during the read. Recheck once; fail closed
      // on continuous changes rather than accepting a quantity that was never checked.
      const now = itemsRef.current.find(i => isSameCartLine(i, newItem.id, newItem.size, newItem.colorVariantId))?.quantity ?? 0;
      if (now !== already) {
        already = now;
        available = await checkCartAddition(newItem.sizeVariantId, already + newItem.quantity);
        if ((itemsRef.current.find(i => isSameCartLine(i, newItem.id, newItem.size, newItem.colorVariantId))?.quantity ?? 0) !== already) throw new Error('cart_changed');
      }
      if (generation !== cartGeneration.current) return { kind: 'error', added: 0 };
      if (available !== null) {
        announceAvailabilityChange();
        showCartAddError(available === 0 ? 'Produto esgotado' : 'Estoque insuficiente',
          available === 0 ? `${newItem.name} (${newItem.size}) não está mais disponível.` : `Apenas ${available} unidade(s) disponível(is). Confira a quantidade no carrinho.`);
        return { kind: available === 0 ? 'soldout' : 'full', added: 0 };
      }
      // Use the just-confirmed quantity, not the stale maxStock from a card.
      newItem = { ...newItem, maxStock: undefined };
    const normalizedItem = { ...newItem, id: normalizeId(newItem.id) };
    const current = itemsRef.current;
    const idx = findExistingIndex(current, normalizedItem);
    const existing = idx >= 0 ? current[idx] : undefined;
    const maxStock = undefined; // Exact total was checked above; never infer a stock ceiling.
    const outcome = planCartAdd(existing?.quantity ?? 0, newItem.quantity, maxStock);
    const notify = opts?.notify !== false;
    if (outcome.added <= 0) {
      if (notify) {
        if (outcome.kind === "soldout") showCartAddError("Produto esgotado", `"${newItem.name}" (${newItem.size}) está sem estoque.`);
        else showCartAddError("Estoque insuficiente", `Você já tem o máximo disponível de "${newItem.name}" (${newItem.size}) no carrinho.`);
      }
      return outcome;
    }
    const updated = existing
      ? current.map((it, i) => i === idx ? { ...it, ...normalizedItem, basePrice: newItem.price, quantity: it.quantity + outcome.added, maxStock } : it)
      : [...current, { ...normalizedItem, quantity: outcome.added }];
    updateItems(() => updated);
    trackEvent("add_to_cart", {
      sourcePage: describeSourcePage(),
      productId: typeof newItem.id === "string" ? newItem.id : null,
      colorVariantId: newItem.colorVariantId || null,
      productName: newItem.name,
      quantity: outcome.added,
      value: newItem.price * outcome.added,
      metadata: { size: newItem.size, color: newItem.colorName || null },
    });
    if (typeof newItem.id === "string") metaAddToCart({ productId: newItem.id, colorVariantId: newItem.colorVariantId || null, name: newItem.name, colorName: newItem.colorName || null, unitPrice: newItem.price, quantity: outcome.added });
    if (notify) {
      const desc = outcome.kind === "adjusted"
        ? `${newItem.name} (${newItem.size}) · só ${outcome.max} disponível(is)`
        : `${newItem.name} · ${newItem.size}`;
      showCartAddedNotice(() => setIsCartOpen(true), desc);
    }
    return outcome;
    } catch {
      showCartAddError('Não foi possível conferir o estoque', 'Nada foi adicionado. Tente novamente ou escolha o tamanho na página do produto.');
      return { kind: 'error', added: 0 };
    } finally { adding.current.delete(key); }
  };

  const matchItem = (item: CartItem, id: string | number, size: string, colorVariantId?: string) =>
    isSameCartLine(item, id, size, colorVariantId);

  const removeItem = (id: string | number, size: string, colorVariantId?: string) => {
    updateItems((prev) => prev.filter((item) => !matchItem(item, id, size, colorVariantId)));
  };

  const updateQuantity = (id: string | number, size: string, quantity: number, colorVariantId?: string) => {
    if (quantity <= 0) {
      removeItem(id, size, colorVariantId);
      return;
    }

    const item = itemsRef.current.find((i) => matchItem(i, id, size, colorVariantId));
    if (!item) return;
    // Enforce stock limit
    if (item.maxStock !== undefined && quantity > item.maxStock) {
        toast({
          title: "Estoque insuficiente",
          description: `Apenas ${item.maxStock} unidade(s) disponível(is) de "${item.name}" (${item.size}).`,
          variant: "destructive",
        });
        return;
    }
    updateItems((prev) => prev.map((i) =>
      matchItem(i, id, size, colorVariantId) ? { ...i, quantity } : i
    ));
  };

  const clearCart = () => {
    cartGeneration.current += 1;
    updateItems(() => []);
    safeStorage.remove(CART_STORAGE_KEY);
    setSavedShippingState(null);
    setSavedPackagingState(null);
    safeStorage.remove(PACKAGING_STORAGE_KEY);
  };

  const updateMaxStock = (id: string | number, size: string, maxStock: number, colorVariantId?: string) => {
    updateItems((prev) =>
      prev.map((item) => {
        if (isSameCartLine(item, id, size, colorVariantId)) {
          const capped = maxStock <= 0 ? 0 : Math.min(item.quantity, maxStock);
          return { ...item, maxStock, quantity: capped };
        }
        return item;
      }).filter(item => item.quantity > 0)
    );
  };

  // ── Regras de desconto por quantidade (leve 2+, cada peça sai por menos) ──
  const { rules: bulkRules, status: quantityRulesStatus, retry: retryQuantityRules } = useQuantityRules("site");

  const cartSignature = items
    .map((i) => `${i.id}|${i.size}|${i.colorVariantId || ""}|${i.colorName || ""}|${i.quantity}|${i.basePrice ?? i.price}`)
    .join(";");

  useEffect(() => {
    updateItems((prev) => {
      if (prev.length === 0) return prev;
      const results = applyQuantityRules(
        prev.map((i) => ({
          productId: i.id,
          basePrice: i.basePrice ?? i.price,
          quantity: i.quantity,
          colorName: i.colorName ?? null,
        })),
        bulkRules
      );
      let changed = false;
      const next = prev.map((item, idx) => {
        const base = item.basePrice ?? item.price;
        const r = results[idx];
        if (item.price === r.unitPrice && item.basePrice === base && (item.bulkRuleName ?? null) === (r.appliedRuleName ?? null) && (item.bulkRuleId ?? null) === (r.appliedRuleId ?? null)) {
          return item;
        }
        changed = true;
        return { ...item, basePrice: base, price: r.unitPrice, bulkRuleName: r.appliedRuleName ?? undefined, bulkRuleId: r.appliedRuleId ?? undefined };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartSignature, bulkRules]);

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const packagingCount = savedPackaging ? savedPackaging.quantity : 0;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0) + packagingCount;

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        updateMaxStock,
        clearCart,
        total,
        itemCount,
        isCartOpen,
        setIsCartOpen,
        savedShipping,
        setSavedShipping,
        savedPackaging,
        setSavedPackaging,
        quantityRulesStatus,
        retryQuantityRules: () => { retryQuantityRules(); },
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
